import React, { useState, useEffect, useRef, useCallback } from "react";
import axios from "axios";
import { config } from "../../config/const";
import "./StoryForm.css";
import { toast } from 'react-toastify';
import { useNavigate, useLocation } from 'react-router-dom';
import 'react-toastify/dist/ReactToastify.css';

const LOADING_TIPS = [
  "Crafting your adventure...",
  "Adding magical twists...",
  "Building the world of your story...",
  "Inventing fun characters...",
  "Sprinkling in surprises...",
  "Weaving an exciting plot...",
  "Polishing the final details..."
];

const ProgressiveImage = ({ src, alt, onRegenerate, index }) => {
  const [loadingProgress, setLoadingProgress] = useState(0);
  const [imageLoaded, setImageLoaded] = useState(false);
  const [imgError, setImgError] = useState(false);

  useEffect(() => {
    setLoadingProgress(0);
    setImageLoaded(false);
    setImgError(false);

    if (!src) {
      // No URL — skip the fake animation and show error immediately
      setImgError(true);
      return;
    }

    const interval = setInterval(() => {
      setLoadingProgress(prev => {
        if (prev >= 100) {
          clearInterval(interval);
          setImageLoaded(true);
          return 100;
        }
        return prev + 5;
      });
    }, 100);

    return () => clearInterval(interval);
  }, [src]);

  if (imgError || !src) {
    return (
      <div className="image-error-card">
        <div className="error-illustration">🎨</div>
        <p>Oops! This image didn't quite work out</p>
        <span className="error-subtext">The image service may be temporarily unavailable</span>
        <button className="retry-button" onClick={() => onRegenerate(index)}>Try Again</button>
      </div>
    );
  }

  return (
    <div className="progressive-image-container">
      <img
        src={src}
        alt={alt}
        className="story-image"
        style={{
          clipPath: `inset(0 0 ${100 - loadingProgress}% 0)`,
          transition: 'clip-path 0.3s ease-out'
        }}
        onLoad={() => setImageLoaded(true)}
        onError={() => setImgError(true)}
      />
      {imageLoaded && (
        <button
          className="regenerate-btn"
          onClick={() => onRegenerate(index)}
          title="Regenerate image"
        >
          <svg
            viewBox="0 0 24 24"
            width="16"
            height="16"
          >
            <path
              fill="currentColor"
              d="M17.65 6.35C16.2 4.9 14.21 4 12 4c-4.42 0-7.99 3.58-7.99 8s3.57 8 7.99 8c3.73 0 6.84-2.55 7.73-6h-2.08c-.82 2.33-3.04 4-5.65 4-3.31 0-6-2.69-6-6s2.69-6 6-6c1.66 0 3.14.69 4.22 1.78L13 11h7V4l-2.35 2.35z"
            />
          </svg>
        </button>
      )}
    </div>
  );
};

const getChildAge = (child) => {
  if (child.age) return child.age;
  if (child.dateOfBirth) {
    const diff = Date.now() - new Date(child.dateOfBirth).getTime();
    return Math.floor(diff / (365.25 * 24 * 60 * 60 * 1000));
  }
  return null;
};

const StoryForm = ({ selectedImages = [] }) => {
  const defaultStoryIdea = "Create a story about a six year old girl named Elara who travels to Egypt to learn about the mummies. Make the story interesting by adding many twists and turns and providing factual information for kids to learn from.";
  
  const defaultVisualIdea ="Elara is a six year old girl who wears a short blue color frock and is from northern India. She has brown curly short hair. Create only cartoon characters without any text written on the image."


  // Update initial state to use default values
  const [storyIdeas, setStoryIdeas] = useState(defaultStoryIdea);
  const [visualIdeas, setVisualIdeas] = useState(defaultVisualIdea);
  const [story, setStory] = useState("");
  const [paragraphs, setParagraphs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [tags, setTags] = useState([]);
  const [title, setTitle] = useState("");
  const [activeTab, setActiveTab] = useState("story"); // eslint-disable-line no-unused-vars
  const [questions, setQuestions] = useState([]); // eslint-disable-line no-unused-vars
  const [email, setEmail] = useState(localStorage.getItem("email")); // eslint-disable-line no-unused-vars
  const [isEditing, setIsEditing] = useState(false);
  const [showForm, setShowForm] = useState(true);
  const [storyWithImages, setStoryWithImages] = useState([]);
  const [selectedThumbnail, setSelectedThumbnail] = useState('');
  const [storyStatus, setStoryStatus] = useState('public');
  const [storyId, setStoryId] = useState(null); // eslint-disable-line no-unused-vars
  const [selectedImagesList, setSelectedImagesList] = useState(selectedImages);
  const [isEditingTitle, setIsEditingTitle] = useState(false);
  const [visualMode, setVisualMode] = useState('prompt'); // eslint-disable-line no-unused-vars
  const [selectedCartoonStyle, setSelectedCartoonStyle] = useState(null);
  const [selectedCartoonImageId, setSelectedCartoonImageId] = useState(null);
  const [children, setChildren] = useState([]);
  let [firstGenerationId, setFirstGenerationId] = useState(''); // eslint-disable-line no-unused-vars
  const firstGenerationIdRef = useRef('');
  const [dropdownVisible, setDropdownVisible] = useState(false); // State to toggle dropdown

  const [selectedVisualOption, setSelectedVisualOption] = useState('create-cartoon');
  const [newTag, setNewTag] = useState('');
  const [loadingTip, setLoadingTip] = useState(LOADING_TIPS[0]);
  const [numParagraphs, setNumParagraphs] = useState(5);
  const [pointsLeft, setPointsLeft] = useState(null);
  const [editingImageIndex, setEditingImageIndex] = useState(null);
  const [editingImagePrompt, setEditingImagePrompt] = useState('');

  const POINTS_PER_PARAGRAPH = 10;

  const cartoonStyleNames = ["Ghibli Style", "Pixar Style", "Disney Style", "Anime Style"];

  const navigate = useNavigate();
  const location = useLocation();
  const selectedChild = location.state?.selectedChild || null;
  const suggestedPrompt = location.state?.suggestedPrompt || null;

  // Auto-configure when a child is selected from PersonaPicker
  useEffect(() => {
    if (selectedChild) {
      // Auto-select the child's first cartoon image if available
      if (selectedChild.cartoonImage && selectedChild.cartoonImage.length > 0) {
        setSelectedVisualOption(selectedChild.cartoonImage[0]);
        setSelectedCartoonStyle(selectedChild.cartoonImage[0]);
        // Use Cloudinary URL (cartoonImage) — not cartoonImageId (Leonardo ID which expires after ~24h)
        if (selectedChild.cartoonImage && selectedChild.cartoonImage.length > 0) {
          setSelectedCartoonImageId(selectedChild.cartoonImage[0]);
        }
      }
    }

    // Use suggested prompt from Knowledge Graph topic, or personalize default
    if (suggestedPrompt) {
      setStoryIdeas(suggestedPrompt);
    } else if (selectedChild) {
      const childName = selectedChild.name;
      const childAge = selectedChild.age;
      const personalizedPrompt = `Create a story about ${childAge ? `a ${childAge} year old` : 'a young'} ${childName ? `child named ${childName}` : 'child'} who travels to Egypt to learn about the mummies. Make the story interesting by adding many twists and turns and providing factual information for kids to learn from.`;
      setStoryIdeas(personalizedPrompt);
    }
  }, [selectedChild]); // eslint-disable-line react-hooks/exhaustive-deps

  // Rotate loading tips
  useEffect(() => {
    if (!loading) return;
    let i = 0;
    const interval = setInterval(() => {
      i = (i + 1) % LOADING_TIPS.length;
      setLoadingTip(LOADING_TIPS[i]);
    }, 3000);
    return () => clearInterval(interval);
  }, [loading]);

  // Update prompt with child's name and age when a pic is selected
  const updatePromptForChild = useCallback((child) => {
    const name = child.name;
    const age = getChildAge(child);
    const ageStr = age ? `a ${age} year old` : 'a young';
    // Only update if user hasn't heavily edited the prompt (check if it still looks like a default/suggested prompt)
    setStoryIdeas(prev => {
      // Replace age/name pattern in existing prompt if it matches the template style
      const templatePattern = /(?:a \d+ year old|a young)\s*(?:child named \w+|\w+)/i;
      const childPattern = /for (?:a child|a \d+ year old|a young)\s*(?:\(age \d+\))?/i;
      if (childPattern.test(prev)) {
        return prev.replace(childPattern, `for ${ageStr} child named ${name}`);
      }
      if (templatePattern.test(prev)) {
        return prev.replace(templatePattern, `${ageStr} child named ${name}`);
      }
      return prev;
    });
  }, []);

  const handleRemoveTag = useCallback((indexToRemove) => {
    setTags(prev => prev.filter((_, i) => i !== indexToRemove));
  }, []);

  const handleAddTag = useCallback((value) => {
    const trimmed = value.trim().replace(/^#/, '');
    if (trimmed && !tags.includes(trimmed)) {
      setTags(prev => [...prev, trimmed]);
    }
    setNewTag('');
  }, [tags]);

  const handleAdvancedOptionsClick = () => {
    navigate("/create-options");
  };

  const convertToBase64 = (blob) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result);
      reader.onerror = reject;
      reader.readAsDataURL(blob);
    });
  };

  const randomSeed = Math.floor(Math.random() * 9999999998) + 1;

  useEffect(() => {
    const fetchChildren = async () => {
      try {
        const response = await axios.get(`${config.backend_url}/user/children`, {
          params: { email }
        });
        setChildren(response.data.children.filter(child => child.cartoonImage));
      } catch (error) {
        console.error('Error fetching children:', error);
      }
    };

    fetchChildren();
  }, [email]);

  useEffect(() => {
    if (children && children.length > 0) {
      const first = children[0];
      if (first && first.cartoonImage) {
        setSelectedVisualOption(first.cartoonImage);
        setSelectedCartoonStyle(first.cartoonImage);
        setSelectedCartoonImageId(first.cartoonImage?.[0] || null); // Use Cloudinary URL, not Leonardo ID
      } else {
        setSelectedVisualOption('create-cartoon');
        setSelectedCartoonStyle(null);
        setSelectedCartoonImageId(null);
      }
    } else {
      setSelectedVisualOption('create-cartoon');
      setSelectedCartoonStyle(null);
      setSelectedCartoonImageId(null);
    }
  }, [children]);

  // Fetch user's remaining points on mount
  useEffect(() => {
    const fetchPoints = async () => {
      try {
        const baseUrl = config?.backend_url || 'http://localhost:3002';
        const response = await axios.get(`${baseUrl}/user/user`, { params: { email } });
        if (response.data?.user?.pointsLeft !== undefined) {
          setPointsLeft(response.data.user.pointsLeft);
        }
      } catch (err) {
        console.error('Error fetching user points:', err);
      }
    };
    if (email) fetchPoints();
  }, [email]);

  const DAILY_STORY_LIMIT = 2;

  // Update handleGenerateStory
  const handleGenerateStory = async () => {
    if (!storyIdeas.trim()) return;

    // Check daily story limit
    try {
      const baseUrl = config?.backend_url || 'http://localhost:3002';
      const storiesRes = await axios.get(`${baseUrl}/user/getStories?email=${email}`);
      const allStories = storiesRes.data.stories || [];
      const startOfToday = new Date();
      startOfToday.setHours(0, 0, 0, 0);
      const todayCount = allStories.filter(s => new Date(s.createdAt) >= startOfToday).length;
      if (todayCount >= DAILY_STORY_LIMIT) {
        toast.warning(`Daily limit reached — you can create ${DAILY_STORY_LIMIT} stories per day. Come back tomorrow!`);
        return;
      }
    } catch (err) {
      console.error('Error checking daily limit:', err);
      // Allow story creation if check fails (graceful degradation)
    }

    setLoading(true);
    setShowForm(false);
    
    try {
      // Convert selected images to base64
      const imagePromises = selectedImagesList.map(async (imageUrl) => {
        try {
          const response = await fetch(imageUrl);
          const blob = await response.blob();
          return await convertToBase64(blob);
        } catch (error) {
          console.error('Error converting image:', error);
          return null;
        }
      });

      const base64Images = await Promise.all(imagePromises);
      const validImages = base64Images.filter(img => img !== null);
      
      const baseUrl = config?.backend_url || 'http://localhost:3002';
      const response = await axios.post(`${baseUrl}/user/generateStory`, {
        email,
        prompt: storyIdeas,
        numParagraphs,
        selectedImages: validImages
      });
      if (response.data) {
        const storyContent = response.data.content;
        setParagraphs(storyContent); // Store paragraphs for later use
        setStory(storyContent.join('\n\n'));
        setTitle(response.data.title);
        setTags(response.data.tag?.split('#').filter(tag => tag.trim()) || []);
        // Update points from response
        if (response.data.pointsLeft !== undefined) {
          setPointsLeft(response.data.pointsLeft);
        }
      }
    } catch (error) {
      console.error("Story Generation Error:", error);
      if (error.response?.status === 403) {
        toast.warning(error.response.data?.message || "Not enough points to generate this story. Try fewer paragraphs or come back tomorrow!");
      } else {
        alert("Our devs ran out of coffee and funding!! No story for now. Inform them and try again tomorrow..");
      }
      setShowForm(true); // Show form again if error occurs
    } finally {
      setLoading(false);
    }
  };

  // Update handleEditSave to preserve paragraph formatting
  const handleEditSave = () => {
    setIsEditing(false);
    // Split by double newline to maintain paragraph separation
    const editedParagraphs = story.split('\n\n').filter(p => p.trim());
    setParagraphs(editedParagraphs);
    // Keep original formatting when joining
    setStory(editedParagraphs.join('\n\n'));
  };

  const handleCreateVisuals = async () => {
    if (!paragraphs.length) return;

    const validParagraphs = paragraphs.filter(p => p.trim());

    setStoryWithImages(validParagraphs.map((text, index) => ({
      text,
      image: null,
      isLoading: true,
      index
    })));

    let firstGenId = null; // To store the styleRefId
    if (selectedCartoonImageId) {
      // Cartoon style selected
      const firstParagraph = validParagraphs[0];
      const firstImageResult = await generateCartoonImageForParagraph(firstParagraph, 0, selectedCartoonImageId, null); 

      if (firstImageResult && firstImageResult.styleRefId) {
        firstGenId = firstImageResult.styleRefId;
        firstGenerationIdRef.current = firstGenId; 
        setFirstGenerationId(firstGenId); 

        setStoryWithImages(prev => prev.map(item =>
          item.index === 0 ? {
            ...item,
            image: firstImageResult.image,
            isLoading: false
          } : item
        ));

        validParagraphs.forEach(async (paragraph, index) => {
          if (index > 0) {
            generateCartoonImageForParagraph(paragraph, index, selectedCartoonImageId, firstGenId);
          }
        });
      } else {
        validParagraphs.forEach(async (paragraph, index) => {
          generateCartoonImageForParagraph(paragraph, index, selectedCartoonImageId);
        });
      }
    } else {
      // Generate images for all paragraphs using the default method
      validParagraphs.forEach(async (paragraph, index) => {
        generateImageForParagraph(paragraph, index, randomSeed);
      });
    }
  };

  const generateCartoonImageForParagraph = async (paragraph, index, imageId, firstGenerationId, retryCount = 0) => {
    const baseUrl = config?.backend_url || 'http://localhost:3002';
    const MAX_AUTO_RETRIES = 2; // cartoon generation can time out — retry up to 2x

    // Guard: if imageId is missing, fall back to regular image generation
    if (!imageId) {
      console.warn(`No imageId provided for paragraph ${index}, falling back to standard generation`);
      return generateImageForParagraph(paragraph, index, randomSeed);
    }

    try {
      let styleRefId = null;

      let requestBody = {
        email,
        imageId: String(imageId),
        paragraphs: [paragraph]
      };

      if (index > 0 && firstGenerationId) {
        // 2nd+ images: pass the first generated image as style reference
        requestBody.styleRefId = firstGenerationId;
      } else if (index === 0 && selectedChild?.profilePicture) {
        // First image: send profile photo so backend can anchor character identity
        requestBody.profileImageUrl = selectedChild.profilePicture;
      }

      const response = await axios.post(`${baseUrl}/user/cartoonImage`, requestBody);

      if (response.data.styleRefId) {
        styleRefId = response.data.styleRefId;
      }

      const cartoonUrl = response.data.images?.[0] || null;

      // If backend returned 200 but image is null, retry before showing error
      if (!cartoonUrl && retryCount < MAX_AUTO_RETRIES) {
        console.warn(`Cartoon image ${index} returned null, retrying (attempt ${retryCount + 2})...`);
        await new Promise(resolve => setTimeout(resolve, 4000));
        return generateCartoonImageForParagraph(paragraph, index, imageId, firstGenerationId, retryCount + 1);
      }

      setStoryWithImages(prev => prev.map(item =>
        item.index === index ? {
          ...item,
          image: cartoonUrl,
          error: !cartoonUrl,
          isLoading: false
        } : item
      ));
      return { styleRefId: styleRefId, image: cartoonUrl };
    } catch (error) {
      const isTimeout = error?.response?.status === 503 && error?.response?.data?.timeout;
      // Full diagnostic trace so devtools console shows exactly what failed
      console.error(`Failed to generate cartoon image ${index} (attempt ${retryCount + 1}):`, {
        status: error?.response?.status,
        statusText: error?.response?.statusText,
        backendMessage: error?.response?.data?.message,
        backendDetail: error?.response?.data?.detail,
        backendFull: error?.response?.data,
        axiosMessage: error?.message,
        requestBody: error?.config?.data,
      });

      // Auto-retry on timeout (503) or network errors
      if (retryCount < MAX_AUTO_RETRIES && (isTimeout || !error?.response)) {
        console.log(`Retrying cartoon image ${index} after timeout/network error...`);
        await new Promise(resolve => setTimeout(resolve, 4000));
        return generateCartoonImageForParagraph(paragraph, index, imageId, firstGenerationId, retryCount + 1);
      }

      setStoryWithImages(prev => prev.map(item =>
        item.index === index ? {
          ...item,
          error: true,
          isLoading: false
        } : item
      ));
      return { styleRefId: null, image: null };
    }
  };

  const generateImageForParagraph = async (paragraph, index, randomSeed, retryCount = 0) => {
    const baseUrl = config?.backend_url || 'http://localhost:3002';
    const MAX_AUTO_RETRIES = 1; // Auto-retry once before showing error

    try {
      const response = await axios.post(`${baseUrl}/user/generateImage`, {
        email,
        visualPrompt: visualIdeas,
        paragraphs: [paragraph],
        seed: randomSeed
      });

      const imageUrl = response.data.images?.[0] || null;

      // If backend returned 200 but image is null (Cloudinary silently failed), treat as error
      if (!imageUrl) {
        if (retryCount < MAX_AUTO_RETRIES) {
          console.warn(`Image ${index} returned null URL, retrying...`);
          await new Promise(resolve => setTimeout(resolve, 3000));
          return generateImageForParagraph(paragraph, index, randomSeed, retryCount + 1);
        }
        setStoryWithImages(prev => prev.map(item =>
          item.index === index ? { ...item, error: true, isLoading: false } : item
        ));
        return;
      }

      // Update individual image slot when ready
      setStoryWithImages(prev => prev.map(item =>
        item.index === index ? {
          ...item,
          image: imageUrl,
          isLoading: false
        } : item
      ));

    } catch (error) {
      console.error(`Failed to generate image ${index} (attempt ${retryCount + 1}):`, error);

      // Auto-retry once before showing error to user
      if (retryCount < MAX_AUTO_RETRIES) {
        console.log(`Auto-retrying image ${index}...`);
        await new Promise(resolve => setTimeout(resolve, 3000));
        return generateImageForParagraph(paragraph, index, randomSeed, retryCount + 1);
      }

      setStoryWithImages(prev => prev.map(item =>
        item.index === index ? {
          ...item,
          error: true,
          isLoading: false
        } : item
      ));
    }
  };

  const handleOpenRegeneratePrompt = (index) => {
    const item = storyWithImages[index];
    setEditingImageIndex(index);
    setEditingImagePrompt(item.text);
  };

  const handleCancelRegeneratePrompt = () => {
    setEditingImageIndex(null);
    setEditingImagePrompt('');
  };

  const handleConfirmRegenerate = (index) => {
    const customPrompt = editingImagePrompt.trim() || storyWithImages[index].text;
    setEditingImageIndex(null);
    setEditingImagePrompt('');
    handleRegenerateImage(index, customPrompt);
  };

  const handleRegenerateImage = async (index, customPrompt) => {

    const paragraphToRegenerate = customPrompt || storyWithImages[index].text;


    setStoryWithImages(prev => prev.map(item =>
      item.index === index ? {
        ...item,
        isLoading: true,
        error: false
      } : item
    ));
    
    try {

      if (selectedCartoonImageId) {
        if (firstGenerationIdRef.current) {
          generateCartoonImageForParagraph(paragraphToRegenerate, index, selectedCartoonImageId, firstGenerationIdRef.current);
        } else {
          generateCartoonImageForParagraph(paragraphToRegenerate, index, selectedCartoonImageId);
        }
      } else {
        generateImageForParagraph(paragraphToRegenerate, index, randomSeed);
      }

    } catch (error) {
      console.error(`Failed to regenerate image ${index}:`, error);
      setStoryWithImages(prev => prev.map(item => 
        item.index === index ? {
          ...item,
          error: true,
          isLoading: false
        } : item
      ));
      toast.error("Failed to regenerate image");
    }
  };

  // Update handleSaveStory with navigation
  const handleSaveStory = async () => {
    try {
      const baseUrl = config?.backend_url || 'http://localhost:3002';
      const token = localStorage.getItem("token");

      const validStoryList = storyWithImages
        .filter(item => item.text.trim())
        .map((item, index) => ({
          line: item.text,
          image: item.image,
          id: index
        }));

      const storyData = {
        email,
        title,
        content: validStoryList.map(item => item.line).join('\n\n'),
        storyList: validStoryList,
        prompt: storyIdeas,
        thumbnail: selectedThumbnail || validStoryList[0]?.image,
        status: storyStatus,
        tag: tags
      };
      
      const headers = {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      };

      const response = await axios.post(`${baseUrl}/user/save`, storyData, { headers });
      toast.success('Story saved! Please provide feedback.');
      setStoryId(response.data.id);
      navigate(`/feedback/${response.data.id}`, {
        state: {
          storyData
        }
      });
      
    } catch (error) {
      toast.error("Failed to save story");
    }
  };

  // Update handleRejectStory with better error handling
  const handleRejectStory = async () => {
    try {
      const baseUrl = config?.backend_url || 'http://localhost:3002';
      const token = localStorage.getItem("idToken");

      // First save the story with rejected status
      const validStoryList = storyWithImages
        .filter(item => item.text.trim())
        .map((item, index) => ({
          line: item.text,
          image: item.image,
          id: index
        }));

      const storyData = {
        email,
        title,
        content: validStoryList.map(item => item.line).join('\n\n'),
        storyList: validStoryList,
        prompt: storyIdeas,
        thumbnail: selectedThumbnail || validStoryList[0]?.image,
        status: 'rejected',
        tag: tags
      };

      const headers = {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      };

      // Save story first to get an ID
      await axios.post(`${baseUrl}/user/save`, storyData, { headers });
      // const newStoryId = saveResponse.data.id;

      // // Then call reject API with the new story ID
      // await axios.post(`${baseUrl}/user/reject-story`, { storyId: newStoryId });
      
      toast.info("Story discarded");
      
      
      // Reset all form states
    setStoryWithImages(selectedImages);
    setStory("");
    setParagraphs([]);
    setTitle("");
    setTags([]);
    setStoryIdeas(defaultStoryIdea);
    setVisualIdeas(defaultVisualIdea);
    setSelectedThumbnail("");
    setStoryStatus("public");
    setIsEditing(false);
    
    // Show the initial form
    setShowForm(true);
    setActiveTab("story");
    
    // Stay on create story page instead of navigating away
    navigate('/create');
    } catch (error) {
      console.error("Reject Story Error:", error);
      toast.error("Failed to reject story");
    } finally {
      setStoryWithImages([]);
      setStory("");
      setParagraphs([]);
      setTitle("");
      setTags([]);
      setShowForm(true);
      setActiveTab("story");
    }
  };

  const handleRemoveImage = (indexToRemove) => {
    setSelectedImagesList(prev => prev.filter((_, index) => index !== indexToRemove));
  };

  const handleThreeDotsClick = () => {
    setDropdownVisible((prev) => !prev); 
  };


  if (loading) {
    return (
      <div className="loader-container">
        <div className="loader-book">
          <div className="book-spine"></div>
          <div className="book-page"></div>
          <div className="book-page"></div>
        </div>
        <p className="loader-text">{loadingTip}</p>
        <div className="loader-progress-bar">
          <div className="loader-progress-fill"></div>
        </div>
      </div>
    );
  }

  // Step progress logic
  const getCurrentStep = () => {
    if (loading) return 2;
    if (!showForm && storyWithImages.length > 0) return 4;
    if (!showForm) return 3;
    return 1;
  };
  const currentStep = getCurrentStep();
  const STEPS = [
    { num: 1, label: 'Idea' },
    { num: 2, label: 'Generate' },
    { num: 3, label: 'Edit' },
    { num: 4, label: 'Publish' },
  ];

  return (
    <>
      <div className="story-form">
        {/* Step progress bar */}
        <div className="step-progress-bar">
          {STEPS.map((step, idx) => (
            <React.Fragment key={step.num}>
              <div className={`step-item ${currentStep === step.num ? 'active' : ''} ${currentStep > step.num ? 'completed' : ''}`}>
                <div className="step-circle">
                  {currentStep > step.num ? '✓' : step.num}
                </div>
                <span className="step-label">{step.label}</span>
              </div>
              {idx < STEPS.length - 1 && (
                <div className={`step-connector ${currentStep > step.num ? 'completed' : ''}`} />
              )}
            </React.Fragment>
          ))}
        </div>

        {selectedChild && showForm && (
          <div className="selected-child-banner">
            <div className="selected-child-avatar">
              {(selectedChild.cartoonImage?.[0] || selectedChild.profilePicture) ? (
                <img
                  src={selectedChild.cartoonImage?.[0] || selectedChild.profilePicture}
                  alt={selectedChild.name}
                  onError={(e) => {
                    e.target.style.display = "none";
                    e.target.nextSibling && (e.target.nextSibling.style.display = "inline");
                  }}
                />
              ) : null}
              <span
                className="selected-child-initial"
                style={{ display: (selectedChild.cartoonImage?.[0] || selectedChild.profilePicture) ? "none" : "inline" }}
              >
                {selectedChild.name.charAt(0).toUpperCase()}
              </span>
            </div>
            <div className="selected-child-info">
              <span className="selected-child-name">
                Writing a story for <strong>{selectedChild.name}</strong>
              </span>
              {selectedChild.age && (
                <span className="selected-child-age">{selectedChild.age} years old</span>
              )}
            </div>
            <button
              className="change-child-btn"
              onClick={() => navigate('/create')}
            >
              Change
            </button>
          </div>
        )}
        {showForm ? (
          <>
            {selectedImagesList.length > 0 && (
              <div className="image-preview-grid">
                {selectedImagesList.map((image, index) => (
                  <div key={index} className="image-thumbnail-container">
                    <button 
                      className="remove-image-btn"
                      onClick={() => handleRemoveImage(index)}
                    >
                      ✕
                    </button>
                    <img src={image} alt={`Upload ${index + 1}`} />
                  </div>
                ))}
              </div>
            )}

            <div className="form-group ideas-box">
              <div className="ideas-header">
                <label className="form-label">Ideas for the story:</label>
                <div className="dropdown-container">
                  <button
                    className="three-dots-button"
                    onClick={handleThreeDotsClick}
                  >
                    ⋮
                  </button>
                  {dropdownVisible && (
                    <div className="dropdown-menu">
                      <button
                        className="advanced-option-button"
                        onClick={handleAdvancedOptionsClick}
                      >
                        Advanced Options
                      </button>
                    </div>
                  )}
                </div>
              </div>
              <textarea
                className="form-textarea"
                placeholder={defaultStoryIdea}
                value={storyIdeas}
                onChange={(e) => setStoryIdeas(e.target.value)}
                rows="6"
              />
            </div>

            <div className="paragraph-slider-group">
              <label className="form-label">Number of paragraphs: <strong>{numParagraphs}</strong></label>
              <div className="slider-row">
                <span className="slider-bound">4</span>
                <input
                  type="range"
                  min="4"
                  max="12"
                  value={numParagraphs}
                  onChange={(e) => setNumParagraphs(parseInt(e.target.value))}
                  className="paragraph-slider"
                />
                <span className="slider-bound">12</span>
              </div>
              <div className="cost-estimate">
                <span className="cost-label">Cost: <strong>{numParagraphs * POINTS_PER_PARAGRAPH} points</strong></span>
                {pointsLeft !== null && (
                  <span className={`points-remaining ${Math.max(0, pointsLeft) < numParagraphs * POINTS_PER_PARAGRAPH ? 'low-points' : ''}`}>
                    {Math.max(0, pointsLeft)} points remaining today
                  </span>
                )}
              </div>
            </div>

            <div className="visual-options">
              <label className="visual-style-label">Pick a picture for creating visuals:</label>
              {selectedVisualOption !== 'prompt' && (
                <div className="cartoon-grid">
                  {(selectedChild
                    ? children.filter(c =>
                        selectedChild._ids
                          ? selectedChild._ids.includes(c._id)
                          : c._id === selectedChild._id
                      )
                    : children
                  ).map(child => (
                    <div className="child-group" key={child._id}>
                      {!selectedChild && <div className="child-group-header">{child.name}</div>}
                      {child.profilePicture && (
                        <div
                          className={`cartoon-option ${selectedCartoonImageId === `profile-${child._id}` ? 'selected' : ''}`}
                          onClick={() => {
                            setSelectedVisualOption(child.profilePicture);
                            setSelectedCartoonStyle('profile');
                            setSelectedCartoonImageId(`profile-${child._id}`);
                            updatePromptForChild(child);
                          }}
                        >
                          <img
                            src={child.profilePicture}
                            alt={`${child.name} Profile`}
                            onError={(e) => { e.target.style.display = "none"; }}
                          />
                          <p>Profile Pic</p>
                        </div>
                      )}
                      {child.cartoonImage && child.cartoonImage.map((img, idx) => (
                        <div
                          key={child._id + '-' + idx}
                          className={`cartoon-option ${selectedCartoonImageId === img ? 'selected' : ''}`}
                          onClick={() => {
                            setSelectedVisualOption(img);
                            setSelectedCartoonStyle(img);
                            setSelectedCartoonImageId(img); // Use Cloudinary URL — not Leonardo ID
                            updatePromptForChild(child);
                          }}
                        >
                          <img src={img} alt={`${child.name} - ${cartoonStyleNames[idx]} Style`} onError={(e) => { e.target.style.display = "none"; }} />
                          <p>{cartoonStyleNames[idx]}</p>
                        </div>
                      ))}
                    </div>
                  ))}
                  <div
                    className={`cartoon-option ${selectedVisualOption === 'create-cartoon' ? 'selected' : ''}`}
                    onClick={() => {
                      setSelectedVisualOption('create-cartoon');
                      setSelectedCartoonStyle(null);
                      setSelectedCartoonImageId(null);
                    }}
                  >
                    <div className="create-avatar-content">
                      <span className="info-icon">＋</span>
                      <p>Add more styles</p>
                      <button
                        type="button"
                        className="create-avatar-link"
                        onClick={(e) => {
                          e.preventDefault();
                          navigate('/profile', {
                            state: {
                              openForm: true,
                              openChildForm: true,
                              scrollTo: 'cartoonify-feature-intro',
                              fromStory: true,
                              returnTo: '/create'
                            }
                          });
                        }}
                      >
                        Go to profile
                      </button>
                    </div>
                  </div>
                  <div
                    className={`cartoon-option ${selectedVisualOption === 'prompt' ? 'selected' : ''}`}
                    onClick={() => {
                      setSelectedVisualOption('prompt');
                      setSelectedCartoonStyle(null);
                      setSelectedCartoonImageId(null);
                    }}
                  >
                    <div className="create-avatar-content">
                      <span className="info-icon">🖊️</span>
                      <p>Generate visuals with text</p>
                    </div>
                  </div>
                </div>
              )}
               {selectedVisualOption === 'prompt' && (
                 <div className="visual-prompt">
                   <textarea
                     className="form-textarea"
                     placeholder={defaultVisualIdea}
                     value={visualIdeas}
                     onChange={(e) => setVisualIdeas(e.target.value)}
                     rows="6"
                   />
                 </div>
               )}
             </div>

             <button
               className="generate-button"
               onClick={() => {
                 // Validation: check story ideas
                 if (!storyIdeas.trim()) {
                   toast.warning("Please enter your story ideas first!");
                   return;
                 }
                 // Validation: check visual selection
                 if (selectedVisualOption === 'create-cartoon') {
                   toast.warning("Please select a character style for your story visuals, or choose 'Generate visuals with text'.");
                   return;
                 }
                 if (selectedVisualOption === 'prompt' && !visualIdeas.trim()) {
                   toast.warning("Please enter a visual description, or select a character style instead.");
                   return;
                 }
                 if (selectedVisualOption !== 'prompt' && !selectedCartoonStyle) {
                   toast.warning("Please select a character style for your story visuals.");
                   return;
                 }
                 handleGenerateStory();
               }}
               disabled={loading}
             >
               {loading ? "Generating..." : "Generate Story"}
             </button>
             {!loading && (selectedVisualOption === 'create-cartoon' || (!selectedCartoonStyle && selectedVisualOption !== 'prompt')) && (
               <p className="generate-hint">
                 ⬆ Please select a character style above, or choose "Generate visuals with text"
               </p>
             )}
          </>
        ) : (
          <div className="story-content">
            <div className="story-section">
              <div className="title-section">
                {isEditingTitle ? (
                  <input
                    type="text"
                    value={title}
                    onChange={(e) => setTitle(e.target.value)}
                    onBlur={() => setIsEditingTitle(false)}
                    onKeyPress={(e) => {
                      if (e.key === 'Enter') {
                        setIsEditingTitle(false);
                      }
                    }}
                    className="title-input"
                    autoFocus
                  />
                ) : (
                  <h2 onClick={() => setIsEditingTitle(true)} className="editable-title">
                    {title || 'Click to add title'}
                    <span className="edit-icon">✎</span>
                  </h2>
                )}
              </div>
              {storyWithImages.length > 0 ? (
                <>
                  {/* Image generation progress counter */}
                  {storyWithImages.some(item => item.isLoading) && (() => {
                    const total = storyWithImages.filter(item => item.text.trim()).length;
                    const done = storyWithImages.filter(item => item.text.trim() && !item.isLoading).length;
                    return (
                      <div className="gen-progress-counter">
                        <span>Generating image {done + 1} of {total}</span>
                        <div className="gen-progress-track">
                          <div className="gen-progress-fill" style={{ width: `${(done / total) * 100}%` }} />
                        </div>
                      </div>
                    );
                  })()}
                  <div className="story-with-visuals">
                    {storyWithImages
                      .filter(item => item.text.trim()) // Only show non-empty paragraphs
                      .map((item, index) => (
                        <React.Fragment key={index}>
                          <div className="paragraph-container">
                            <div className="story-text">{item.text}</div>
                          </div>
                          <div className="visual-container">
                            {item.isLoading ? (
                              <div className="image-loader">
                                <div className="spinner"></div>
                                <p>Generating image...</p>
                              </div>
                            ) : item.error ? (
                              <div className="image-error-card">
                                <div className="error-illustration">🎨</div>
                                <p>Oops! This image didn't quite work out</p>
                                <span className="error-subtext">The image service may be temporarily unavailable</span>
                                <button className="retry-button" onClick={() => handleRegenerateImage(index)}>
                                  Try Again
                                </button>
                              </div>
                            ) : editingImageIndex === index ? (
                              <div className="regenerate-prompt-editor">
                                <label className="regenerate-prompt-label">Edit prompt for this image:</label>
                                <textarea
                                  className="regenerate-prompt-textarea"
                                  value={editingImagePrompt}
                                  onChange={(e) => setEditingImagePrompt(e.target.value)}
                                  rows="3"
                                />
                                <div className="regenerate-prompt-actions">
                                  <button className="regenerate-prompt-cancel" onClick={handleCancelRegeneratePrompt}>Cancel</button>
                                  <button className="regenerate-prompt-confirm" onClick={() => handleConfirmRegenerate(index)}>Regenerate</button>
                                </div>
                              </div>
                            ) : (
                              <div className="image-wrapper">
                                <ProgressiveImage
                                  src={item.image}
                                  alt={`Story visual ${index + 1}`}
                                  onRegenerate={() => handleOpenRegeneratePrompt(index)}
                                  index={index}
                                />
                              </div>
                            )}
                          </div>
                        </React.Fragment>
                      ))}
                  </div>
                </>
              ) : (
                <div className="story-box">
                  {isEditing ? (
                    <textarea
                      value={story}
                      onChange={(e) => setStory(e.target.value)}
                      className="story-textarea"
                    />
                  ) : (
                    <div className="story-text">
                      {story}
                    </div>
                  )}
                </div>
              )}
              <div className="tags-container">
                {tags.map((tag, index) => (
                  <span key={index} className="tag">
                    #{tag}
                    <button
                      className="tag-remove"
                      onClick={() => handleRemoveTag(index)}
                      title="Remove tag"
                    >×</button>
                  </span>
                ))}
                <div className="tag-input-wrapper">
                  <input
                    type="text"
                    className="tag-input"
                    placeholder="+ Add tag"
                    value={newTag}
                    onChange={(e) => setNewTag(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ',') {
                        e.preventDefault();
                        handleAddTag(newTag);
                      }
                    }}
                    onBlur={() => { if (newTag.trim()) handleAddTag(newTag); }}
                  />
                </div>
              </div>
              <div className="action-buttons">
                {!storyWithImages.length ? (
                  <>
                    <button 
                      onClick={() => {
                        if (isEditing) {
                          handleEditSave();  // Call handleEditSave when saving edits
                        } else {
                          setIsEditing(true);
                        }
                      }} 
                      // disabled={generatingVisuals}
                    >
                      {isEditing ? "Save" : "Edit Story"}
                    </button>
                    <button onClick={handleCreateVisuals} disabled={isEditing}>
                      Create Visuals
                    </button>
                  </>
                ) : (
                  <div className="final-actions">
                    {storyWithImages.some(item => item.image) && (
                      <div className="thumbnail-selector">
                        <h3>Select Thumbnail</h3>
                        <div className="thumbnail-grid">
                          {storyWithImages.map((item, index) =>
                            item.image ? (
                              <img
                                key={index}
                                src={item.image}
                                alt={`Thumbnail option ${index + 1}`}
                                onClick={() => setSelectedThumbnail(item.image)}
                                className={selectedThumbnail === item.image ? 'selected' : ''}
                              />
                            ) : (
                              <div key={index} className="thumbnail-placeholder">
                                No image
                              </div>
                            )
                          )}
                        </div>
                      </div>
                    )}
                    <div className="visibility-selector">
                      <label className="status-label">Choose Story Visibility</label>
                      <div className="visibility-options">
                        <button
                          className={`visibility-option ${storyStatus === 'public' ? 'active' : ''}`}
                          onClick={() => setStoryStatus('public')}
                        >
                          <span className="visibility-icon">🌐</span>
                          <span className="visibility-label">Public</span>
                          <span className="visibility-desc">Anyone can view</span>
                        </button>
                        <button
                          className={`visibility-option ${storyStatus === 'private' ? 'active' : ''}`}
                          onClick={() => setStoryStatus('private')}
                        >
                          <span className="visibility-icon">🔒</span>
                          <span className="visibility-label">Private</span>
                          <span className="visibility-desc">Only you can view</span>
                        </button>
                      </div>
                    </div>
                    <div className="story-actions">
                      <button className="discard-button" onClick={handleRejectStory}>Discard</button>
                      <button className="save-button" onClick={handleSaveStory}>Save Story</button>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
      </div>
    </>
  );
};

export default StoryForm;
